<template>
  <div class="contract-massive-screen pa-2">
    <!-- CABECERA SUPERIOR CON TABS -->
    <v-card elevation="1" class="mb-2 rounded">
      <v-toolbar color="primary" dark dense height="42">
        <v-icon small left>mdi-account-multiple-plus</v-icon>
        <span class="text-subtitle-2 font-weight-bold">
          Contratos Masivos
        </span>
        <v-spacer></v-spacer>

        <!-- Indicadores según pestaña activa -->
        <template v-if="activeMainTab === 0">
          <v-chip
            small
            color="white"
            text-color="primary"
            class="font-weight-bold mr-2"
            v-if="selectedRequirement"
          >
            <v-icon left x-small>mdi-briefcase</v-icon>
            Req #{{ selectedRequirement.ReqID }}:
            {{ selectedRequirement.PstName || selectedRequirement.ReqPstName }}
          </v-chip>

          <v-chip
            small
            color="success lighten-1"
            text-color="white"
            class="font-weight-bold mr-3"
            v-if="selectedWorkers.length > 0"
          >
            <v-icon left x-small>mdi-account-check</v-icon>
            Lote: {{ selectedWorkers.length }} trabajador(es)
          </v-chip>
        </template>
        <template v-else>
          <v-chip
            small
            :color="
              renewalForm.operationType === 'EXTENSION'
                ? 'blue darken-2'
                : renewalForm.operationType === 'RENOVATION'
                ? 'deep-orange darken-2'
                : 'red darken-2'
            "
            text-color="white"
            class="font-weight-bold mr-3"
            v-if="selectedActiveWorkers.length > 0"
          >
            <v-icon left x-small>{{
              renewalForm.operationType === "EXTENSION"
                ? "mdi-fast-forward"
                : renewalForm.operationType === "RENOVATION"
                ? "mdi-autorenew"
                : "mdi-account-minus"
            }}</v-icon>
            Seleccionados: {{ selectedActiveWorkers.length }} trabajador(es)
          </v-chip>
        </template>

        <v-btn icon small @click="goBack">
          <v-icon small>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-tabs
        v-model="activeMainTab"
        background-color="grey lighten-4"
        color="primary"
        dense
        slider-color="primary"
      >
        <v-tab class="font-weight-bold text-caption">
          <v-icon small left>mdi-account-plus</v-icon>
          NUEVO CONTRATO MASIVO
        </v-tab>
        <v-tab class="font-weight-bold text-caption">
          <v-icon small left>mdi-autorenew</v-icon>
          AMPLIACIÓN / RENOVACIÓN / BAJA MASIVA
        </v-tab>
      </v-tabs>
    </v-card>

    <!-- ========================================================================= -->
    <!-- TAB 0: NUEVO CONTRATO MASIVO (REQUERIMIENTO + CREACIÓN) -->
    <!-- ========================================================================= -->
    <div v-show="activeMainTab === 0">
      <v-row dense>
        <!-- ================= COLUMNA IZQUIERDA: REQUERIMIENTOS Y SELECCIÓN DE TRABAJADORES ================= -->
        <v-col cols="12" lg="7" md="7">
          <!-- 1. SECCIÓN SUPERIOR: SELECCIÓN DE REQUERIMIENTOS -->
          <v-card outlined class="mb-2 rounded">
            <div
              class="px-3 pt-2 pb-1 grey lighten-4 d-flex align-center flex-wrap"
            >
              <span class="text-caption font-weight-bold primary--text mr-3">
                <v-icon x-small color="primary" class="mr-1"
                  >mdi-format-list-checks</v-icon
                >
                1. SELECCIONAR REQUERIMIENTO (OBLIGATORIO)
              </span>
            </div>

            <!-- Filtros de Requerimiento -->
            <div class="px-3 pt-2 pb-1">
              <v-row dense align="center">
                <v-col cols="12" sm="4">
                  <s-select-campaign-se
                    label="Campaña"
                    v-model="filterReq.CmpID"
                    dense
                    clearable
                    @input="applyRequirementFilters"
                  />
                </v-col>
                <v-col cols="12" sm="4">
                  <s-select-area
                    v-model="filterReq.AreID"
                    label="Área"
                    dense
                    clearable
                    @input="applyRequirementFilters"
                  />
                </v-col>
                <v-col cols="12" sm="4">
                  <s-select-position
                    v-model="filterReq.PstID"
                    :AreID="filterReq.AreID || 0"
                    label="Cargo"
                    dense
                    clearable
                    @input="applyRequirementFilters"
                  />
                </v-col>
              </v-row>
            </div>

            <!-- Tabla de Requerimientos -->
            <v-data-table
              dense
              :headers="requirementHeaders"
              :items="filteredRequirements"
              item-key="ReqID"
              :loading="loadingRequirements"
              class="elevation-0 requirement-table border-top"
              height="160px"
              fixed-header
              hide-default-footer
              :items-per-page="50"
              no-data-text="No hay requerimientos activos disponibles con los filtros seleccionados."
              @click:row="selectRequirement"
            >
              <!-- Fila seleccionada highlight -->
              <template v-slot:item="{ item }">
                <tr
                  :class="{
                    'selected-req-row':
                      selectedRequirement &&
                      selectedRequirement.ReqID === item.ReqID,
                  }"
                  @click="selectRequirement(item)"
                  style="cursor: pointer"
                >
                  <td class="font-weight-bold text-center">
                    <v-icon
                      x-small
                      color="primary"
                      v-if="
                        selectedRequirement &&
                        selectedRequirement.ReqID === item.ReqID
                      "
                      >mdi-arrow-right-bold</v-icon
                    >
                    {{ item.ReqID }}
                  </td>
                  <td class="text-center">
                    <v-chip
                      x-small
                      :color="
                        item.TypeRequirement === 1
                          ? 'purple lighten-4'
                          : 'blue lighten-4'
                      "
                      :text-color="
                        item.TypeRequirement === 1
                          ? 'purple darken-3'
                          : 'blue darken-3'
                      "
                      class="font-weight-bold"
                    >
                      {{ item.TypeRequirement === 1 ? "Prof." : "Masivo" }}
                    </v-chip>
                  </td>
                  <td class="text-truncate" style="max-width: 120px">
                    {{ item.AreName || "-" }}
                  </td>
                  <td class="text-truncate" style="max-width: 120px">
                    {{ item.PstName || item.ReqPstName || "-" }}
                  </td>
                  <td class="text-truncate" style="max-width: 90px">
                    {{ item.CmpName || "-" }}
                  </td>
                  <td class="text-center font-weight-medium">
                    {{ item.ReqSalary ? `S/ ${item.ReqSalary}` : "-" }}
                  </td>
                  <td class="text-center font-weight-medium">
                    {{ item.ReqQuantity || 0 }}
                  </td>
                  <td class="text-center">
                    {{ item.CoveredCount || item.CntAten || 0 }}
                  </td>
                  <td class="text-center font-weight-bold success--text">
                    {{
                      selectedRequirement &&
                      selectedRequirement.ReqID === item.ReqID
                        ? selectedWorkers.length
                        : 0
                    }}
                  </td>
                </tr>
              </template>
            </v-data-table>
          </v-card>

          <!-- 2. SECCIÓN INFERIOR: SELECTOR DE PERSONAL (S-CRUD BIOMÉTRICO -> BOTONES -> LOTE CON VALIDACIÓN) -->
          <v-card outlined class="rounded pa-2">
            <v-row dense align="center">
              <!-- TABLA IZQUIERDA: S-CRUD TRABAJADORES (COMO EN BIOMÉTRICOS) -->
              <v-col cols="12" md="6" sm="12">
                <s-crud
                  :single-row="false"
                  :add="false"
                  noEdit
                  saveList
                  addNoFunction
                  searchInput
                  :rowDefault="false"
                  @rowSelected="onWorkersSelectedFromGrid($event)"
                  ref="gridWorker"
                  title="Trabajadores Disponibles"
                  :config="configWorker"
                  :filter="filterWorker"
                  @doubleClick="assignWorkerFromGrid($event)"
                >
                  <template v-slot:filter>
                    <v-container>
                      <v-row justify="center">
                        <v-col lg="6">
                          <s-select-area
                            v-model="filterWorker.AreID"
                            label="Área"
                          />
                        </v-col>
                      </v-row>
                    </v-container>
                  </template>
                </s-crud>
              </v-col>

              <!-- BOTONES CENTRALES DE TRASPASO (> y <) -->
              <v-col
                cols="12"
                md="1"
                sm="12"
                class="d-flex flex-md-column justify-center align-center gap-2 py-2"
              >
                <v-tooltip right>
                  <template v-slot:activator="{ on }">
                    <v-btn
                      v-on="on"
                      color="primary"
                      depressed
                      small
                      class="mb-md-2"
                      :disabled="gridSelectedWorkers.length === 0"
                      @click="moveGridSelectedToBatch"
                    >
                      <v-icon small>mdi-chevron-right</v-icon>
                    </v-btn>
                  </template>
                  <span>Agregar seleccionados al lote</span>
                </v-tooltip>

                <v-tooltip right>
                  <template v-slot:activator="{ on }">
                    <v-btn
                      v-on="on"
                      color="error"
                      outlined
                      small
                      :disabled="checkedBatchIndexes.length === 0"
                      @click="moveCheckedToAvailable"
                    >
                      <v-icon small>mdi-chevron-left</v-icon>
                    </v-btn>
                  </template>
                  <span>Quitar del lote</span>
                </v-tooltip>
              </v-col>

              <!-- LISTA DERECHA: TRABAJADORES EN EL LOTE -->
              <v-col cols="12" md="5" sm="12">
                <v-card outlined class="rounded">
                  <div
                    class="pa-2 grey lighten-5 border-bottom d-flex align-center"
                  >
                    <v-checkbox
                      v-model="checkAllBatch"
                      dense
                      hide-details
                      class="mt-0 pt-0 mr-2"
                      @change="toggleSelectAllBatch"
                    ></v-checkbox>
                    <span class="caption font-weight-bold"
                      >Trabajador en Lote ({{ selectedWorkers.length }})</span
                    >
                    <v-spacer></v-spacer>
                    <v-btn
                      icon
                      x-small
                      color="primary"
                      :loading="isValidating"
                      @click="validateBatchWorkers"
                    >
                      <v-icon x-small>mdi-refresh</v-icon>
                    </v-btn>
                  </div>
                  <!-- Buscador lote -->
                  <div class="px-2 pt-1">
                    <v-text-field
                      v-model="searchBatch"
                      dense
                      outlined
                      placeholder="Filtrar lote..."
                      hide-details
                      prepend-inner-icon="mdi-magnify"
                      class="caption-input"
                    ></v-text-field>
                  </div>
                  <!-- Lista con scroll y badges de estado -->
                  <div class="worker-list-scroll">
                    <v-list dense class="py-0">
                      <v-list-item-group v-model="checkedBatchIndexes" multiple>
                        <template v-for="(w, idx) in filteredBatchWorkers">
                          <v-list-item
                            :key="w.WkrID || idx"
                            class="px-2 py-0 min-height-32"
                          >
                            <template v-slot:default="{ active }">
                              <v-list-item-action class="my-0 mr-2">
                                <v-checkbox
                                  :input-value="active"
                                  dense
                                  hide-details
                                ></v-checkbox>
                              </v-list-item-action>
                              <v-list-item-content class="py-1">
                                <v-list-item-title
                                  class="caption font-weight-medium text-truncate"
                                >
                                  {{
                                    w.WkrName ||
                                    w.WorkerFullName ||
                                    w.WorkerName ||
                                    w.PrsFullName ||
                                    w.NtpFullName ||
                                    (w.PrsDocumentNumber
                                      ? `Personal DNI: ${w.PrsDocumentNumber}`
                                      : `Trabajador #${w.WkrID}`)
                                  }}
                                </v-list-item-title>
                                <div
                                  class="d-flex align-center gap-1 mt-1 flex-wrap"
                                >
                                  <span class="x-small grey--text mr-1"
                                    >Doc: {{ w.PrsDocumentNumber }}</span
                                  >
                                  <!-- Badge Banco -->
                                  <v-chip
                                    x-small
                                    :color="
                                      w.HasBank
                                        ? 'success lighten-4'
                                        : 'error lighten-4'
                                    "
                                    :text-color="
                                      w.HasBank
                                        ? 'success darken-3'
                                        : 'error darken-3'
                                    "
                                  >
                                    {{ w.HasBank ? "Bco" : "Sin Bco" }}
                                  </v-chip>
                                  <!-- Badge Pensión -->
                                  <v-chip
                                    x-small
                                    :color="
                                      w.HasPension
                                        ? 'primary lighten-4'
                                        : 'error lighten-4'
                                    "
                                    :text-color="
                                      w.HasPension
                                        ? 'primary darken-3'
                                        : 'error darken-3'
                                    "
                                  >
                                    {{ w.HasPension ? "AFP" : "Sin AFP" }}
                                  </v-chip>
                                  <!-- Badge Salud -->
                                  <v-chip
                                    x-small
                                    :color="
                                      w.HasHealth
                                        ? 'teal lighten-4'
                                        : 'error lighten-4'
                                    "
                                    :text-color="
                                      w.HasHealth
                                        ? 'teal darken-3'
                                        : 'error darken-3'
                                    "
                                  >
                                    {{ w.HasHealth ? "Salud" : "Sin Salud" }}
                                  </v-chip>
                                  <!-- Badge Solapamiento -->
                                  <v-chip
                                    v-if="w.HasOverlap"
                                    x-small
                                    color="error"
                                    text-color="white"
                                    class="font-weight-bold"
                                  >
                                    Solapado
                                  </v-chip>
                                </div>
                              </v-list-item-content>
                            </template>
                          </v-list-item>
                          <v-divider
                            :key="`batch-div-${idx}`"
                            class="my-0"
                          ></v-divider>
                        </template>
                      </v-list-item-group>
                    </v-list>
                    <div
                      v-if="filteredBatchWorkers.length === 0"
                      class="text-center py-4 caption grey--text"
                    >
                      Lote vacío. Agregue personal seleccionando en la tabla y
                      pulsando '>' o con doble clic.
                    </div>
                  </div>
                </v-card>
              </v-col>
            </v-row>
          </v-card>
        </v-col>

        <!-- ================= COLUMNA DERECHA: FORMULARIO DE CONDICIONES LABORALES (SOLO OBLIGATORIOS) ================= -->
        <v-col cols="12" lg="5" md="5">
          <v-card
            outlined
            class="pa-3 rounded h-100 d-flex flex-column justify-space-between"
          >
            <div>
              <div class="pb-2 mb-2 border-bottom d-flex align-center">
                <v-icon small color="primary" class="mr-1"
                  >mdi-file-document-edit</v-icon
                >
                <span class="text-subtitle-2 font-weight-bold primary--text">
                  3. DATOS LABORALES DEL CONTRATO
                </span>
                <v-spacer></v-spacer>
                <v-chip
                  x-small
                  color="info lighten-4"
                  text-color="info darken-3"
                  v-if="selectedRequirement"
                >
                  Autocompletado por Req #{{ selectedRequirement.ReqID }}
                </v-chip>
              </div>

              <!-- Fila 1: Planilla y Centro de Costo -->
              <v-row dense>
                <v-col cols="12" sm="6">
                  <s-select-definition
                    :def="1060"
                    label="Planilla *"
                    v-model="form.TypePayroll"
                    :disabled="isRequirementLocked"
                    dense
                    :add-custom="$fun.isAdmin()"
                  />
                </v-col>
                <v-col cols="12" sm="6">
                  <s-select-area
                    v-model="form.CceID"
                    :text="form.CceName"
                    centercost
                    label="Centro de Costo *"
                    :disabled="isRequirementLocked"
                    dense
                    full
                  />
                </v-col>
              </v-row>

              <!-- Fila 2: Cargo y Categoría -->
              <v-row dense class="mt-1">
                <v-col cols="12" sm="6">
                  <s-select-position
                    v-model="form.PstID"
                    :text="form.PstName"
                    :AreID="form.AreID || 0"
                    label="Cargo *"
                    :disabled="isRequirementLocked"
                    dense
                    full
                    @input="onPositionChanged"
                  />
                </v-col>
                <v-col cols="12" sm="6">
                  <s-select-definition
                    :def="1482"
                    label="Categoría *"
                    v-model="form.TypeCategory"
                    dense
                    :add-custom="$fun.isAdmin()"
                  />
                </v-col>
              </v-row>

              <!-- Fila 3: Campaña y Tipo de Contrato -->
              <v-row dense class="mt-1">
                <v-col cols="12" sm="6">
                  <s-select-campaign-se
                    ref="campaignRef"
                    label="Campaña *"
                    v-model="form.CmpID"
                    :disabled="isRequirementLocked"
                    dense
                    @datesLoaded="onCampaignDatesLoaded"
                  />
                </v-col>
                <v-col cols="12" sm="6">
                  <s-select-definition
                    ref="typeContractRef"
                    :def="1069"
                    label="Tipo Contrato *"
                    v-model="form.TypeContract"
                    dense
                    :add-custom="$fun.isAdmin()"
                    @change="onTypeContractChanged"
                    @updateData="onTypeContractItemsLoaded"
                    @returnItem="
                      onTypeContractChanged(form.TypeContract, $event)
                    "
                  />
                </v-col>
              </v-row>

              <!-- Fila 4: Tipo Trabajador y Cat. Ocupacional -->
              <v-row dense class="mt-1">
                <v-col cols="12" sm="6">
                  <s-select-definition
                    :def="1039"
                    :dgr="4"
                    label="Tipo Trabajador *"
                    v-model="form.TypeWorker"
                    dense
                    :add-custom="$fun.isAdmin()"
                  />
                </v-col>
                <v-col cols="12" sm="6">
                  <s-select-definition
                    :def="1039"
                    label="Cat. Ocupacional *"
                    v-model="form.TypeOccupationalCategory"
                    dense
                    :add-custom="$fun.isAdmin()"
                  />
                </v-col>
              </v-row>

              <!-- Fila 5: Situación (Costo) y Sueldo -->
              <v-row dense class="mt-1">
                <v-col cols="12" sm="6">
                  <s-select-definition
                    :def="1045"
                    label="Situación (Costo) *"
                    v-model="form.TypeSituation"
                    :disabled="isSituationDisabled"
                    dense
                    :add-custom="$fun.isAdmin()"
                  />
                </v-col>
                <v-col cols="12" sm="6">
                  <s-protected-admin-field
                    ref="salaryProtectedField"
                    label="Sueldo *"
                    v-model="form.AgtSalary"
                    :encrypted="false"
                    :pstID="Number(form.PstID || 0)"
                    :canViewByDefault="$fun.isAdmin()"
                    :alwaysMasked="false"
                    dense
                  />
                </v-col>
              </v-row>

              <!-- Fila 6: Área y Estado -->
              <v-row dense class="mt-1">
                <v-col cols="12" sm="6">
                  <s-select-area
                    v-model="form.AreID"
                    :text="form.AreName"
                    label="Área *"
                    :disabled="isRequirementLocked"
                    dense
                    full
                  />
                </v-col>
                <v-col cols="12" sm="6">
                  <s-text
                    value="REGISTRADO"
                    label="Estado del Contrato"
                    readonly
                    disabled
                  />
                </v-col>
              </v-row>

              <!-- Fila 7: Cuadrilla, Turno y Jornada Máxima -->
              <v-row dense class="mt-1" align="center">
                <v-col cols="12" sm="5">
                  <s-select
                    label="Cuadrilla"
                    :items="quadrille"
                    item-text="GngName"
                    item-value="GngID"
                    v-model="form.GngID"
                    clearable
                    dense
                  />
                </v-col>
                <v-col cols="12" sm="4">
                  <s-select-definition
                    :def="1278"
                    label="Turno *"
                    v-model="form.TypeTurno"
                    dense
                    :add-custom="$fun.isAdmin()"
                  />
                </v-col>
                <v-col cols="12" sm="3">
                  <v-switch
                    v-model="form.AgtMaximunDay"
                    color="primary"
                    hide-details
                    dense
                    class="mt-0"
                    label="Jornada Máx"
                  />
                </v-col>
              </v-row>

              <!-- Fila 8: Línea de Negocio y Fecha Ingreso -->
              <v-row dense class="mt-1">
                <v-col cols="12" sm="6">
                  <s-select-business-line
                    v-model="form.BslID"
                    :text="form.BslName"
                    :cceID="form.CceID"
                    label="Línea de Negocio"
                    :allline="1"
                    dense
                    full
                  />
                </v-col>
                <v-col cols="12" sm="6">
                  <s-date
                    v-model="form.AgtAdmissionDate"
                    label="Fecha Ingreso *"
                    dense
                  />
                </v-col>
              </v-row>

              <!-- Fila 9: Régimen Laboral e Inicio Contrato -->
              <v-row dense class="mt-1">
                <v-col cols="12" sm="6">
                  <s-select-definition
                    :def="1481"
                    label="Régimen Laboral *"
                    v-model="form.TypeLaborRegime"
                    :disabled="isRequirementLocked"
                    dense
                    :add-custom="$fun.isAdmin()"
                  />
                </v-col>
                <v-col cols="12" sm="6">
                  <s-date
                    v-model="form.AgtBeginDate"
                    label="Inicio Contrato *"
                    dense
                    @change="validateBatchWorkers"
                  />
                </v-col>
              </v-row>

              <!-- Fila 10: Situación Especial y Fin Contrato -->
              <v-row dense class="mt-1">
                <v-col cols="12" sm="6">
                  <s-select-definition
                    :def="1070"
                    label="Situación Especial"
                    v-model="form.TypeSpecialSituation"
                    dense
                    :add-custom="$fun.isAdmin()"
                  />
                </v-col>
                <v-col cols="12" sm="6" v-if="!form._isIndeterminate">
                  <s-date
                    ref="endDateRef"
                    v-model="form.AgtEndDate"
                    label="Fin Contrato *"
                    :disabled="isEndDateDisabled"
                    :nullable="true"
                    dense
                    @change="validateBatchWorkers"
                  />
                </v-col>
              </v-row>
            </div>

            <!-- BOTÓN GUARDAR -->
            <div class="pt-4 border-top text-right">
              <v-btn
                color="primary"
                depressed
                large
                class="px-6 font-weight-bold"
                :disabled="
                  selectedWorkers.length === 0 ||
                  !selectedRequirement ||
                  isSaving
                "
                :loading="isSaving"
                @click="confirmMassiveSave"
              >
                <v-icon left>mdi-content-save</v-icon>
                Guardar Contratos en Alta ({{ selectedWorkers.length }})
              </v-btn>
            </div>
          </v-card>
        </v-col>
      </v-row>
    </div>

    <!-- ========================================================================= -->
    <!-- TAB 1: RENOVACIÓN / AMPLIACIÓN MASIVA -->
    <!-- ========================================================================= -->
    <div v-show="activeMainTab === 1">
      <!-- 1. BARRA DE CONFIGURACIÓN DE OPERACIÓN (AMPLIACIÓN / RENOVACIÓN) -->
      <v-card outlined class="mb-2 rounded pa-3">
        <v-row dense align="center">
          <!-- TIPO DE OPERACIÓN TOGGLE -->
          <v-col cols="12" md="4" sm="12">
            <div
              class="text-caption font-weight-bold grey--text text--darken-2 mb-1"
            >
              TIPO DE OPERACIÓN:
            </div>
            <v-btn-toggle
              v-model="renewalForm.operationType"
              mandatory
              dense
              class="w-100 d-flex"
            >
              <v-btn
                value="EXTENSION"
                :color="
                  renewalForm.operationType === 'EXTENSION' ? 'primary' : ''
                "
                :dark="renewalForm.operationType === 'EXTENSION'"
                class="flex-grow-1 font-weight-bold text-caption"
                small
              >
                <v-icon small left>mdi-fast-forward</v-icon>
                Ampliación
              </v-btn>
              <v-btn
                value="RENOVATION"
                :color="
                  renewalForm.operationType === 'RENOVATION'
                    ? 'deep-orange'
                    : ''
                "
                :dark="renewalForm.operationType === 'RENOVATION'"
                class="flex-grow-1 font-weight-bold text-caption"
                small
              >
                <v-icon small left>mdi-autorenew</v-icon>
                Renovación
              </v-btn>
              <v-btn
                value="CESSATION"
                :color="
                  renewalForm.operationType === 'CESSATION' ? 'error' : ''
                "
                :dark="renewalForm.operationType === 'CESSATION'"
                class="flex-grow-1 font-weight-bold text-caption"
                small
              >
                <v-icon small left>mdi-account-minus</v-icon>
                Baja Masiva
              </v-btn>
            </v-btn-toggle>
          </v-col>

          <!-- CAMPOS DE FECHA SEGÚN OPERACIÓN -->
          <v-col cols="12" md="5" sm="12">
            <!-- CASO AMPLIACIÓN: SOLO NUEVA FECHA FIN -->
            <div v-if="renewalForm.operationType === 'EXTENSION'">
              <div class="text-caption font-weight-bold primary--text mb-1">
                <v-icon x-small color="primary" class="mr-1"
                  >mdi-calendar-edit</v-icon
                >
                NUEVA FECHA DE FIN DE CONTRATO (OBLIGATORIO):
              </div>
              <s-date
                v-model="renewalForm.newEndDate"
                label="Nueva Fecha Fin *"
                dense
              />
            </div>

            <!-- CASO RENOVACIÓN: FECHA INICIO Y FECHA FIN -->
            <div v-else-if="renewalForm.operationType === 'RENOVATION'">
              <div class="text-caption font-weight-bold deep-orange--text mb-1">
                <v-icon x-small color="deep-orange" class="mr-1"
                  >mdi-calendar-sync</v-icon
                >
                VIGENCIA DEL NUEVO CONTRATO:
              </div>
              <v-row dense>
                <v-col cols="6">
                  <s-date
                    v-model="renewalForm.newBeginDate"
                    label="Fec. Inicio *"
                    dense
                  />
                </v-col>
                <v-col cols="6">
                  <s-date
                    v-model="renewalForm.newEndDate"
                    label="Fec. Fin *"
                    dense
                  />
                </v-col>
              </v-row>
            </div>

            <!-- CASO BAJA MASIVA: FECHA DE CESE Y MOTIVO -->
            <div v-else-if="renewalForm.operationType === 'CESSATION'">
              <div class="text-caption font-weight-bold error--text mb-1">
                <v-icon x-small color="error" class="mr-1"
                  >mdi-calendar-remove</v-icon
                >
                DATOS DE CESE / BAJA DE CONTRATOS:
              </div>
              <v-row dense>
                <v-col cols="12" sm="6">
                  <s-date
                    v-model="renewalForm.terminationDate"
                    label="Fecha de Baja *"
                    dense
                  />
                </v-col>
                <v-col cols="12" sm="6">
                  <s-select-definition
                    :def="1051"
                    label="Motivo de Baja"
                    v-model="renewalForm.typeTermination"
                    dense
                    clearable
                    :add-custom="$fun.isAdmin()"
                  />
                </v-col>
              </v-row>
            </div>
          </v-col>

          <!-- BOTÓN PRINCIPAL DE EJECUCIÓN -->
          <v-col cols="12" md="3" sm="12" class="text-right">
            <v-btn
              :color="
                renewalForm.operationType === 'EXTENSION'
                  ? 'primary'
                  : renewalForm.operationType === 'RENOVATION'
                  ? 'deep-orange'
                  : 'error'
              "
              dark
              depressed
              block
              height="48"
              class="font-weight-bold text-caption"
              :disabled="
                !canProcessRenewalExtension || isProcessingRenewalExtension
              "
              :loading="isProcessingRenewalExtension"
              @click="confirmMassiveRenewalExtension"
            >
              <v-icon left small>
                {{
                  renewalForm.operationType === "EXTENSION"
                    ? "mdi-fast-forward"
                    : renewalForm.operationType === "RENOVATION"
                    ? "mdi-autorenew"
                    : "mdi-account-minus"
                }}
              </v-icon>
              {{
                renewalForm.operationType === "EXTENSION"
                  ? "Ampliar"
                  : renewalForm.operationType === "RENOVATION"
                  ? "Renovar"
                  : "Dar de Baja"
              }}
              ({{ selectedActiveWorkers.length }} Sel.)
            </v-btn>
          </v-col>
        </v-row>

        <!-- BANNER INFORMATIVO -->
        <v-alert
          dense
          text
          :color="
            renewalForm.operationType === 'EXTENSION'
              ? 'primary'
              : renewalForm.operationType === 'RENOVATION'
              ? 'deep-orange'
              : 'error'
          "
          class="mt-2 mb-0 py-1 px-3 text-caption rounded"
          border="left"
        >
          <div v-if="renewalForm.operationType === 'EXTENSION'">
            <strong>Modo Ampliación:</strong> Se modificará la
            <em>Fecha Fin</em> de los contratos activos seleccionados
            extendiendo su vigencia (el estado permanece en
            <strong>Alta</strong>). La nueva fecha debe ser mayor a la fecha fin
            actual.
          </div>
          <div v-else-if="renewalForm.operationType === 'RENOVATION'">
            <strong>Modo Renovación:</strong> El contrato anterior pasará a
            <strong>Baja</strong> (con fecha de cese igual a un día antes de la
            nueva fecha de inicio) y se creará automáticamente un
            <strong>Nuevo Contrato</strong>
            heredando todas las condiciones laborales activas (Sueldo, Planilla,
            Banco, AFP, Salud).
          </div>
          <div v-else-if="renewalForm.operationType === 'CESSATION'">
            <strong>Modo Baja Masiva:</strong> El contrato activo de los
            trabajadores seleccionados pasará a estado <strong>Baja</strong> con
            la fecha de cese y motivo indicados. El periodo laboral se marcará
            como cerrado/inactivo y se registrará el movimiento de baja
            respectivo.
          </div>
        </v-alert>
      </v-card>

      <!-- 2. S-CRUD Y LISTA DE SELECCIONADOS -->
      <v-card outlined class="rounded pa-2">
        <v-row dense>
          <!-- S-CRUD DE TRABAJADORES ACTIVOS -->
          <v-col cols="12" md="8" sm="12">
            <s-crud
              :single-row="false"
              :add="false"
              noEdit
              saveList
              addNoFunction
              searchInput
              :rowDefault="false"
              @rowSelected="onActiveWorkersSelectedFromGrid($event)"
              ref="gridActiveWorker"
              title="Trabajadores con Contrato Activo"
              :config="configActiveWorker"
              :filter="filterActiveWorker"
            >
              <template v-slot:filter>
                <v-container fluid class="pa-2">
                  <v-row dense>
                    <v-col cols="12" sm="4">
                      <s-select-campaign-se
                        label="Campaña"
                        v-model="filterActiveWorker.CmpID"
                        dense
                        clearable
                      />
                    </v-col>
                    <v-col cols="12" sm="4">
                      <s-select-area
                        v-model="filterActiveWorker.AreID"
                        label="Área"
                        dense
                        clearable
                      />
                    </v-col>
                    <v-col cols="12" sm="4">
                      <s-select-position
                        v-model="filterActiveWorker.PstID"
                        :AreID="filterActiveWorker.AreID || 0"
                        label="Cargo"
                        dense
                        clearable
                      />
                    </v-col>
                  </v-row>
                </v-container>
              </template>
            </s-crud>
          </v-col>

          <!-- LISTA DE TRABAJADORES SELECCIONADOS PARA PROCESAR -->
          <v-col cols="12" md="4" sm="12">
            <v-card outlined class="rounded h-100 d-flex flex-column">
              <div
                class="px-3 py-2 grey lighten-4 border-bottom d-flex align-center"
              >
                <span class="text-caption font-weight-bold primary--text">
                  <v-icon x-small color="primary" class="mr-1"
                    >mdi-account-check</v-icon
                  >
                  Lote Seleccionado ({{ selectedActiveWorkers.length }})
                </span>
                <v-spacer></v-spacer>
                <v-btn
                  x-small
                  text
                  color="error"
                  :disabled="selectedActiveWorkers.length === 0"
                  @click="clearActiveWorkersSelection"
                >
                  <v-icon x-small left>mdi-trash-can</v-icon>
                  Limpiar
                </v-btn>
              </div>

              <!-- CONTENEDOR CON SCROLL DE SELECCIONADOS -->
              <div
                class="flex-grow-1 overflow-y-auto pa-2"
                style="max-height: 480px"
              >
                <div
                  v-if="selectedActiveWorkers.length === 0"
                  class="text-center grey--text text-caption py-8"
                >
                  <v-icon large color="grey lighten-1" class="d-block mb-2"
                    >mdi-account-multiple-outline</v-icon
                  >
                  Seleccione trabajadores con el checkbox de la tabla para
                  agregarlos a la operación.
                </div>

                <v-list dense class="py-0" v-else>
                  <v-list-item
                    v-for="(w, idx) in selectedActiveWorkers"
                    :key="w.WkrID || idx"
                    class="px-2 mb-1 border-bottom"
                  >
                    <v-list-item-content class="py-1">
                      <v-list-item-title class="text-caption font-weight-bold">
                        {{ w.PrsDocumentNumber }} —
                        {{ w.WkrName || w.NtpFullName }}
                      </v-list-item-title>
                      <v-list-item-subtitle
                        class="x-small d-flex align-center flex-wrap gap-1 mt-1"
                      >
                        <span
                          v-if="w.AreName"
                          class="grey--text text--darken-1 mr-1"
                        >
                          {{ w.AreName }}
                        </span>
                        <span class="mr-1">
                          Fin actual:
                          <strong>{{
                            w.CtrEndDate || w.AgtEndDate
                              ? $fun.formatDateView(
                                  w.CtrEndDate || w.AgtEndDate,
                                )
                              : "Indeterminado"
                          }}</strong>
                        </span>

                        <!-- Validación para operaciones -->
                        <template
                          v-if="renewalForm.operationType === 'EXTENSION'"
                        >
                          <v-chip
                            v-if="!renewalForm.newEndDate"
                            x-small
                            color="grey lighten-3"
                            class="x-small font-weight-bold"
                          >
                            Sin fecha fin
                          </v-chip>
                          <v-chip
                            v-else-if="isWorkerExtensionValid(w)"
                            x-small
                            color="green lighten-4"
                            text-color="green darken-4"
                            class="x-small font-weight-bold"
                          >
                            Válido
                          </v-chip>
                          <v-chip
                            v-else
                            x-small
                            color="red lighten-4"
                            text-color="red darken-4"
                            class="x-small font-weight-bold"
                          >
                            Fecha menor/igual
                          </v-chip>
                        </template>
                        <template
                          v-else-if="renewalForm.operationType === 'RENOVATION'"
                        >
                          <v-chip
                            x-small
                            color="deep-orange lighten-4"
                            text-color="deep-orange darken-4"
                            class="x-small font-weight-bold"
                          >
                            Listo p/ renovar
                          </v-chip>
                        </template>
                        <template
                          v-else-if="renewalForm.operationType === 'CESSATION'"
                        >
                          <v-chip
                            v-if="!renewalForm.terminationDate"
                            x-small
                            color="grey lighten-3"
                            class="x-small font-weight-bold"
                          >
                            Sin fecha baja
                          </v-chip>
                          <v-chip
                            v-else-if="isWorkerCessationValid(w)"
                            x-small
                            color="error lighten-4"
                            text-color="error darken-4"
                            class="x-small font-weight-bold"
                          >
                            Listo p/ baja
                          </v-chip>
                          <v-chip
                            v-else
                            x-small
                            color="red lighten-4"
                            text-color="red darken-4"
                            class="x-small font-weight-bold"
                          >
                            Fecha menor a inicio
                          </v-chip>
                        </template>
                      </v-list-item-subtitle>
                    </v-list-item-content>
                    <v-list-item-action class="my-0">
                      <v-btn
                        icon
                        x-small
                        @click="removeSelectedActiveWorker(idx)"
                      >
                        <v-icon x-small color="grey">mdi-close</v-icon>
                      </v-btn>
                    </v-list-item-action>
                  </v-list-item>
                </v-list>
              </div>
            </v-card>
          </v-col>
        </v-row>
      </v-card>
    </div>

    <!-- DIÁLOGO DE RESULTADOS TRAS GUARDAR (TAB 0) -->
    <v-dialog v-model="showResultDialog" max-width="800px" persistent>
      <v-card>
        <v-toolbar color="primary" dark dense>
          <v-icon left>mdi-clipboard-check</v-icon>
          <v-toolbar-title class="text-subtitle-2 font-weight-bold">
            Resultado de la Creación de Contratos Masivos
          </v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon small @click="showResultDialog = false">
            <v-icon small>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-card-text class="pt-4">
          <div class="d-flex align-center justify-space-around mb-3">
            <v-chip color="primary" outlined class="font-weight-bold">
              Total Procesados: {{ lastResult.TotalRequested || 0 }}
            </v-chip>
            <v-chip color="success" class="font-weight-bold">
              <v-icon left small>mdi-check</v-icon>
              Creados en ALTA: {{ lastResult.SuccessCount || 0 }}
            </v-chip>
            <v-chip
              color="error"
              class="font-weight-bold"
              v-if="lastResult.ErrorCount > 0"
            >
              <v-icon left small>mdi-alert</v-icon>
              Observados: {{ lastResult.ErrorCount }}
            </v-chip>
          </div>

          <v-data-table
            dense
            :headers="resultHeaders"
            :items="lastResult.Details || []"
            item-key="WkrID"
            class="elevation-1"
          >
            <template v-slot:item.Success="{ item }">
              <v-chip
                x-small
                :color="item.Success ? 'success' : 'error'"
                text-color="white"
                class="font-weight-bold"
              >
                {{ item.Success ? "ALTA EXITOSA" : "ERROR" }}
              </v-chip>
            </template>
            <template v-slot:item.AgtID="{ item }">
              <span v-if="item.AgtID" class="font-weight-bold primary--text"
                >#{{ item.AgtID }}</span
              >
              <span v-else class="grey--text">--</span>
            </template>
          </v-data-table>
        </v-card-text>

        <v-card-actions class="px-4 pb-4">
          <v-spacer></v-spacer>
          <v-btn color="primary" depressed @click="onFinishProcess">
            Aceptar y Finalizar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- DIÁLOGO DE RESULTADOS PARA RENOVACIÓN / AMPLIACIÓN (TAB 1) -->
    <v-dialog v-model="showRenewalResultDialog" max-width="850px" persistent>
      <v-card>
        <v-toolbar
          :color="
            renewalForm.operationType === 'EXTENSION'
              ? 'primary'
              : renewalForm.operationType === 'RENOVATION'
              ? 'deep-orange'
              : 'error'
          "
          dark
          dense
        >
          <v-icon left small>{{
            renewalForm.operationType === "EXTENSION"
              ? "mdi-fast-forward"
              : renewalForm.operationType === "RENOVATION"
              ? "mdi-autorenew"
              : "mdi-account-minus"
          }}</v-icon>
          <v-toolbar-title class="text-subtitle-2 font-weight-bold">
            Resultado de
            {{
              renewalForm.operationType === "EXTENSION"
                ? "Ampliación"
                : renewalForm.operationType === "RENOVATION"
                ? "Renovación"
                : "Baja"
            }}
            Masiva
          </v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon small @click="showRenewalResultDialog = false">
            <v-icon small>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-card-text class="pt-4">
          <div class="d-flex align-center justify-space-around mb-3">
            <v-chip color="primary" outlined class="font-weight-bold">
              Total Solicitados: {{ renewalResult.TotalRequested || 0 }}
            </v-chip>
            <v-chip color="success" class="font-weight-bold">
              <v-icon left small>mdi-check</v-icon>
              Procesados con Éxito: {{ renewalResult.SuccessCount || 0 }}
            </v-chip>
            <v-chip
              color="error"
              class="font-weight-bold"
              v-if="renewalResult.ErrorCount > 0"
            >
              <v-icon left small>mdi-alert</v-icon>
              Observados / Errores: {{ renewalResult.ErrorCount }}
            </v-chip>
          </div>

          <v-data-table
            dense
            :headers="renewalResultHeaders"
            :items="renewalResult.Details || []"
            item-key="WkrID"
            class="elevation-1"
          >
            <template v-slot:item.Success="{ item }">
              <v-chip
                x-small
                :color="item.Success ? 'success' : 'error'"
                text-color="white"
                class="font-weight-bold"
              >
                {{ item.Success ? "EXITOSO" : "ERROR" }}
              </v-chip>
            </template>
            <template v-slot:item.AgtID="{ item }">
              <span v-if="item.AgtID" class="font-weight-bold primary--text">
                #{{ item.AgtID }}
              </span>
              <span v-else class="grey--text">--</span>
            </template>
          </v-data-table>
        </v-card-text>

        <v-card-actions class="px-4 pb-4">
          <v-spacer></v-spacer>
          <v-btn
            :color="
              renewalForm.operationType === 'EXTENSION'
                ? 'primary'
                : renewalForm.operationType === 'RENOVATION'
                ? 'deep-orange'
                : 'error'
            "
            dark
            depressed
            @click="onFinishRenewalProcess"
          >
            Aceptar y Finalizar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import _sStaffingRequirement from "@/services/HumanResource/RecruitmentSelection/HmnStaffingRequirement.js";
import _sCardService from "@/services/HumanResource/CardService.js";
import _sWorkerService from "@/services/HumanResource/WorkerService.js";
import _sCttoService from "@/services/HumanResource/HmnContractService.js";
import _sQuadriService from "@/services/HumanResource/hmnGang.js";
import _sPositionService from "@/services/General/PositionService.js";
import SProtectedAdminField from "@/components/Utils/RecursosHm/sProtectedAdminField.vue";

export default {
  name: "ContractMassiveAdd",

  components: {
    SProtectedAdminField,
  },

  data() {
    return {
      activeMainTab: 0,
      quickWorkerSelected: null,
      filterAllRequirements: false,
      filterReq: { CmpID: null, AreID: null, PstID: null },
      requirements: [],
      selectedRequirement: null,
      loadingRequirements: false,

      gridSelectedWorkers: [],
      configWorker: {
        service: _sWorkerService,
        model: {
          WkrID: "ID",
        },
        headers: [
          { text: "DNI", value: "PrsDocumentNumber", sortable: true },
          { text: "Trabajador", value: "WkrName", sortable: false },
          { text: "Área", value: "AreName", sortable: true },
        ],
      },
      filterWorker: {
        AreID: 0,
        NoAssigned: false,
        BieID: 0,
      },

      // Lista de personal disponible y lote seleccionado
      availableWorkers: [],
      selectedWorkers: [],
      checkedAvailableIndexes: [],
      checkedBatchIndexes: [],
      checkAllAvailable: false,
      checkAllBatch: false,
      searchAvailable: "",
      searchBatch: "",
      searchAvailableDebounceTimer: null,
      loadingWorkers: false,
      isValidating: false,
      isSaving: false,

      quadrille: [],
      showResultDialog: false,
      lastResult: {
        TotalRequested: 0,
        SuccessCount: 0,
        ErrorCount: 0,
        Details: [],
      },

      // Headers Requerimientos
      requirementHeaders: [
        {
          text: "N° Requerimiento",
          value: "ReqID",
          align: "center",
          width: "75px",
        },
        {
          text: "Tipo",
          value: "TypeRequirementName",
          align: "center",
          width: "80px",
        },
        { text: "Área", value: "AreName" },
        { text: "Cargo", value: "PstName" },
        { text: "Campaña", value: "CmpName" },
        { text: "Sueldo", value: "ReqSalary", align: "center" },
        { text: "Cnt. Req", value: "ReqQuantity", align: "center" },
        { text: "Cnt. Aten", value: "CoveredCount", align: "center" },
        { text: "Cnt. Selec", value: "SelectedCount", align: "center" },
      ],

      // Headers Resultados
      resultHeaders: [
        { text: "Documento", value: "PrsDocumentNumber" },
        { text: "Trabajador", value: "WorkerFullName" },
        { text: "Contrato", value: "AgtID" },
        { text: "Estado", value: "Success" },
        { text: "Detalle", value: "Message" },
      ],

      // TAB 1: Renovaciones, Ampliaciones y Bajas Masivas
      renewalForm: {
        operationType: "EXTENSION", // "EXTENSION", "RENOVATION", "CESSATION"
        newBeginDate: new Date().toISOString().substring(0, 10),
        newEndDate: "",
        terminationDate: new Date().toISOString().substring(0, 10),
        typeTermination: null,
      },
      configActiveWorker: {
        service: _sWorkerService,
        model: {
          WkrID: "ID",
          CtrBeginDate: "date",
          CtrEndDate: "date",
        },
        headers: [
          {
            text: "DNI",
            value: "PrsDocumentNumber",
            sortable: true,
            width: "100px",
          },
          { text: "Trabajador", value: "WkrName", sortable: true },
          { text: "Área", value: "AreName", sortable: true },
          {
            text: "Inicio Contrato",
            value: "CtrBeginDate",
            sortable: true,
            width: "115px",
          },
          {
            text: "Fin Contrato Actual",
            value: "CtrEndDate",
            sortable: true,
            width: "125px",
          },
        ],
      },
      filterActiveWorker: {
        CtrStatus: 1, // 1: Solo con contrato ACTIVO
        AreID: 0,
        CmpID: 0,
        PstID: 0,
        TypePayroll: 0,
        TypeTurn: 0,
        NoAssigned: false,
        BieID: 0,
      },
      selectedActiveWorkers: [],
      isProcessingRenewalExtension: false,
      showRenewalResultDialog: false,
      renewalResult: {
        TotalRequested: 0,
        SuccessCount: 0,
        ErrorCount: 0,
        Details: [],
      },
      renewalResultHeaders: [
        { text: "DNI", value: "PrsDocumentNumber", width: "100px" },
        { text: "Trabajador", value: "WorkerFullName" },
        { text: "Contrato", value: "AgtID", width: "110px", align: "center" },
        {
          text: "Resultado",
          value: "Success",
          width: "120px",
          align: "center",
        },
        { text: "Detalle / Observación", value: "Message" },
      ],

      // Formulario con los campos de la imagen
      form: {
        EstID: 1,
        TypePayroll: 1,
        CceID: null,
        CceName: "",
        PstID: null,
        PstName: "",
        TypeCategory: 1, // TRABAJADOR
        CmpID: null,
        TypeContract: 11, // DE TEMPORADA
        TypeWorker: 1, // OBRERO / EMPLEADO
        TypeOccupationalCategory: 4, // OBRERO
        TypeSituation: 2, // COSTO VARIABLE
        AgtSalary: "1500.00",
        AreID: null,
        AreName: "",
        GngID: null,
        TypeTurno: 1, // DÍA
        AgtMaximunDay: true, // JORNADA MÁXIMA ON
        BslID: null,
        BslName: "",
        AgtAdmissionDate: new Date().toISOString().substr(0, 10),
        TypeLaborRegime: 3, // RÉGIMEN PRIVADO GENERAL
        AgtBeginDate: new Date().toISOString().substr(0, 10),
        TypeSpecialSituation: 1, // NINGUNA
        AgtEndDate: null,
        _endDateLocked: true,
        _isSeason: true,
        _isIndeterminate: false,
        CmpEndDate: null,
      },
    };
  },

  watch: {
    activeMainTab(val) {
      if (
        val === 1 &&
        this.$refs.gridActiveWorker &&
        this.$refs.gridActiveWorker.reload
      ) {
        this.$nextTick(() => {
          this.$refs.gridActiveWorker.reload();
        });
      }
    },
  },

  computed: {
    isRequirementLocked() {
      return !!(
        this.selectedRequirement &&
        Number(this.selectedRequirement.ReqID || 0) > 0
      );
    },

    isEndDateDisabled() {
      if (this.form._isIndeterminate) return true;
      if (
        this.form._isSeason ||
        this.form.TypeContract === 11 ||
        this.form.TypeContract === "11"
      ) {
        return true;
      }
      return !!this.form._endDateLocked;
    },

    isSituationDisabled() {
      if (this.form._isIndeterminate) return true;
      if (
        this.form._isSeason ||
        this.form.TypeContract === 11 ||
        this.form.TypeContract === "11"
      ) {
        return true;
      }
      return !!this.form._endDateLocked;
    },

    filteredRequirements() {
      return this.requirements.filter((r) => {
        if (Number(r.ReqStatus) !== 5) return false;
        if (this.filterReq.CmpID && r.CmpID !== this.filterReq.CmpID)
          return false;
        if (this.filterReq.AreID && r.AreID !== this.filterReq.AreID)
          return false;
        const pst = r.ReqPstID || r.PstID;
        if (this.filterReq.PstID && pst !== this.filterReq.PstID) return false;
        return true;
      });
    },

    filteredAvailableWorkers() {
      const batchIds = new Set(
        this.selectedWorkers.map((w) => String(w.WkrID || w.PrsDocumentNumber)),
      );
      let list = this.availableWorkers.filter(
        (w) =>
          !batchIds.has(String(w.WkrID)) &&
          !batchIds.has(String(w.PrsDocumentNumber)),
      );
      if (!this.searchAvailable) return list;
      const q = this.searchAvailable.toLowerCase().trim();
      return list.filter((w) => {
        const name = (
          w.WorkerFullName ||
          w.WorkerName ||
          w.NtpFullName ||
          ""
        ).toLowerCase();
        const doc = String(w.PrsDocumentNumber || "").toLowerCase();
        return name.includes(q) || doc.includes(q);
      });
    },

    filteredBatchWorkers() {
      if (!this.searchBatch) return this.selectedWorkers;
      const q = this.searchBatch.toLowerCase().trim();
      return this.selectedWorkers.filter((w) => {
        const name = (
          w.WorkerFullName ||
          w.WorkerName ||
          w.NtpFullName ||
          ""
        ).toLowerCase();
        const doc = String(w.PrsDocumentNumber || "").toLowerCase();
        return name.includes(q) || doc.includes(q);
      });
    },

    canProcessRenewalExtension() {
      if (
        !this.selectedActiveWorkers ||
        this.selectedActiveWorkers.length === 0
      )
        return false;
      if (this.renewalForm.operationType === "EXTENSION") {
        if (!this.renewalForm.newEndDate) return false;
        return true;
      }
      if (this.renewalForm.operationType === "RENOVATION") {
        if (!this.renewalForm.newBeginDate || !this.renewalForm.newEndDate)
          return false;
        if (this.renewalForm.newEndDate < this.renewalForm.newBeginDate)
          return false;
        return true;
      }
      if (this.renewalForm.operationType === "CESSATION") {
        if (!this.renewalForm.terminationDate) return false;
        return true;
      }
      return false;
    },
  },

  mounted() {
    this.loadRequirements();
    this.loadAvailableWorkers();
    this.loadQuadrille();
  },

  methods: {
    goBack() {
      this.$router.push("/recursoshumanos/adminpersonal/registrotrabajadores");
    },

    async loadRequirements() {
      this.loadingRequirements = true;
      try {
        const res = await _sStaffingRequirement.list(this.$fun.getUserID());
        if (res && res.data && Array.isArray(res.data)) {
          const approved = res.data.filter((r) => Number(r.ReqStatus) === 5);
          this.requirements = approved.map((r) => {
            let reqSalary = "";
            if (r.ReqPayValue1) {
              reqSalary =
                this.$fun && this.$fun.decryptSalary
                  ? this.$fun.decryptSalary(r.ReqPayValue1)
                  : r.ReqPayValue1;
            }
            const isProf = Number(r.TypeRequirement) === 1;
            return {
              ...r,
              ReqID: r.ReqID,
              TypeRequirement: r.TypeRequirement || (r.CmpID ? 2 : 1),
              TypeRequirementName: isProf ? "Profesional" : "Masivo",
              AreID: r.AreID,
              AreName: r.AreName,
              CceID: r.CceID,
              CceName: r.CceName,
              BslID: r.BslID,
              BslName: r.BslName,
              PstName: r.PstName || r.ReqPstName,
              CmpName: r.CmpName,
              ReqQuantity: r.ReqQuantity || 0,
              CoveredCount: r.CoveredCount || 0,
              ReqSalary: reqSalary,
            };
          });
        }
      } catch (e) {
        console.warn("Error cargando requerimientos:", e);
      } finally {
        this.loadingRequirements = false;
      }
    },

    async loadAvailableWorkers(searchQuery = "") {
      this.loadingWorkers = true;
      try {
        const dtr = {
          start: 0,
          length: 500,
          search: { value: searchQuery || "" },
          filter: { AreID: 0, NoAssigned: false, BieID: 0 },
        };
        const res = await _sWorkerService.pagination(
          dtr,
          this.$fun.getUserID(),
        );
        if (res && res.data && res.data.data) {
          const loaded = (res.data.data || []).map((w) => {
            const name =
              w.WkrName ||
              w.WorkerName ||
              w.WorkerFullName ||
              w.PrsFullName ||
              w.NtpFullName ||
              [w.NtpPaternalSurname, w.NtpMaternalSurname, w.NtpName]
                .filter(Boolean)
                .join(" ");
            const doc =
              w.PrsDocumentNumber ||
              w.AdmDocumentNumber ||
              w.CrdDocumentNumber ||
              w.PrsDocNumber ||
              w.DocNumber ||
              "";
            return {
              WkrID: w.WkrID || w.NtpID,
              NtpID: w.NtpID,
              PrsDocumentNumber: doc,
              WorkerFullName: name,
              WorkerName: name,
              HasBank: true,
              HasPension: true,
              HasHealth: true,
              HasOverlap: false,
              IsEligible: true,
            };
          });

          const batchIds = new Set(
            this.selectedWorkers.map((s) =>
              String(s.WkrID || s.PrsDocumentNumber),
            ),
          );
          const filteredLoaded = loaded.filter(
            (w) =>
              !batchIds.has(String(w.WkrID)) &&
              !batchIds.has(String(w.PrsDocumentNumber)),
          );

          if (searchQuery) {
            filteredLoaded.forEach((fw) => {
              if (
                !this.availableWorkers.some(
                  (aw) =>
                    String(aw.WkrID) === String(fw.WkrID) ||
                    (fw.PrsDocumentNumber &&
                      String(aw.PrsDocumentNumber) ===
                        String(fw.PrsDocumentNumber)),
                )
              ) {
                this.availableWorkers.push(fw);
              }
            });
          } else {
            this.availableWorkers = filteredLoaded;
          }
        }
      } catch (e) {
        console.warn("Error cargando trabajadores disponibles:", e);
      } finally {
        this.loadingWorkers = false;
      }
    },

    onSearchAvailableInput(val) {
      if (this.searchAvailableDebounceTimer) {
        clearTimeout(this.searchAvailableDebounceTimer);
      }
      if (!val || val.trim() === "") {
        this.loadAvailableWorkers();
        return;
      }
      this.searchAvailableDebounceTimer = setTimeout(() => {
        if (val && val.trim().length >= 2) {
          this.fetchWorkersFromServer(val.trim());
        }
      }, 350);
    },

    onClearSearchAvailable() {
      this.searchAvailable = "";
      this.loadAvailableWorkers();
    },

    async fetchWorkersFromServer(query) {
      if (!query) return;
      this.loadingWorkers = true;
      try {
        // 1. Buscar en paginación principal con CardService
        await this.loadAvailableWorkers(query);

        // 2. Consultar maestro de trabajadores vía workerce para asegurar que aparezcan también si están en otros estados
        const resList = await _sWorkerService.searchWorkersByList(
          query,
          0,
          this.$fun.getUserID(),
        );
        if (resList && resList.data && Array.isArray(resList.data)) {
          const batchIds = new Set(
            this.selectedWorkers.map((s) =>
              String(s.WkrID || s.PrsDocumentNumber),
            ),
          );
          resList.data.forEach((w) => {
            const name =
              w.WkrName ||
              w.WorkerName ||
              w.WorkerFullName ||
              w.PrsFullName ||
              w.NtpFullName ||
              [w.NtpPaternalSurname, w.NtpMaternalSurname, w.NtpName]
                .filter(Boolean)
                .join(" ");
            const doc =
              w.PrsDocumentNumber ||
              w.AdmDocumentNumber ||
              w.PrsDocNumber ||
              "";
            const wkrObj = {
              WkrID: w.WkrID,
              NtpID: w.NtpID,
              PrsDocumentNumber: doc,
              WorkerFullName: name,
              WorkerName: name,
              HasBank: true,
              HasPension: true,
              HasHealth: true,
              HasOverlap: false,
              IsEligible: true,
            };
            if (
              !batchIds.has(String(wkrObj.WkrID)) &&
              !batchIds.has(String(wkrObj.PrsDocumentNumber)) &&
              !this.availableWorkers.some(
                (aw) =>
                  String(aw.WkrID) === String(wkrObj.WkrID) ||
                  (doc && String(aw.PrsDocumentNumber) === String(doc)),
              )
            ) {
              this.availableWorkers.push(wkrObj);
            }
          });
        }
      } catch (e) {
        console.warn("Error buscando trabajadores en servidor:", e);
      } finally {
        this.loadingWorkers = false;
      }
    },

    loadQuadrille() {
      _sQuadriService
        .list({ SecStatus: 1 }, this.$fun.getUserID())
        .then((resp) => {
          if (resp && resp.data) {
            this.quadrille = resp.data;
          }
        })
        .catch((e) => console.warn("Error cargando cuadrillas:", e));
    },

    applyRequirementFilters() {
      // computed se actualiza automáticamente
    },

    async selectRequirement(item) {
      if (!item) return;
      this.selectedRequirement = item;

      // Autocompletar datos laborales desde el requerimiento en lista
      if (item.TypePayroll) this.form.TypePayroll = item.TypePayroll;
      if (item.AreID) {
        this.form.AreID = item.AreID;
        this.form.AreName = item.AreName || "";
      }
      if (item.CceID) {
        this.form.CceID = item.CceID;
        this.form.CceName = item.CceName || "";
      }
      if (item.BslID) {
        this.form.BslID = item.BslID;
        this.form.BslName = item.BslName || "";
      }
      if (item.ReqPstID || item.PstID) {
        this.form.PstID = item.ReqPstID || item.PstID;
        this.form.PstName = item.PstName || item.ReqPstName || "";
      }
      if (item.CmpID) this.form.CmpID = item.CmpID;
      if (item.TypeLaborRegime)
        this.form.TypeLaborRegime = item.TypeLaborRegime;

      // Sueldo: para masivo se toma del requerimiento; para profesional se verifica que tenga sueldo
      let salary = item.ReqSalary || "";
      if (!salary && item.ReqPayValue1) {
        salary =
          this.$fun && this.$fun.decryptSalary
            ? this.$fun.decryptSalary(item.ReqPayValue1)
            : item.ReqPayValue1;
      }

      if (salary && !isNaN(Number(salary)) && Number(salary) > 0) {
        this.form.AgtSalary = String(salary);
      }

      if (item.ReqEndContract) {
        this.form.AgtEndDate = String(item.ReqEndContract).substring(0, 10);
      }

      // Obtener detalle completo por ID para asegurar CceID, CceName, BslID, BslName y demás campos
      try {
        const detailRes = await _sStaffingRequirement.byID(
          item.ReqID,
          this.$fun.getUserID(),
        );
        if (detailRes && detailRes.data) {
          const detail = detailRes.data;
          if (detail.CceID) {
            this.form.CceID = detail.CceID;
            this.form.CceName = detail.CceName || this.form.CceName;
          }
          if (detail.BslID) {
            this.form.BslID = detail.BslID;
            this.form.BslName = detail.BslName || this.form.BslName;
          }
          if (detail.AreID) {
            this.form.AreID = detail.AreID;
            this.form.AreName = detail.AreName || this.form.AreName;
          }
          if (detail.ReqPstID || detail.PstID) {
            this.form.PstID = detail.ReqPstID || detail.PstID;
            this.form.PstName =
              detail.PstName || detail.ReqPstName || this.form.PstName;
          }
          if (detail.TypePayroll) this.form.TypePayroll = detail.TypePayroll;
          if (detail.TypeLaborRegime)
            this.form.TypeLaborRegime = detail.TypeLaborRegime;
          if (detail.CmpID) this.form.CmpID = detail.CmpID;
          if (detail.CmpEndDate) {
            this.form.CmpEndDate = String(detail.CmpEndDate).substring(0, 10);
          }
          if (detail.ReqEndContract) {
            this.form.AgtEndDate = String(detail.ReqEndContract).substring(
              0,
              10,
            );
          }
          if (
            this.form._isSeason ||
            this.form.TypeContract === 11 ||
            this.form.TypeContract === "11" ||
            this.form._endDateLocked
          ) {
            if (this.form.CmpEndDate) {
              this.form.AgtEndDate = this.form.CmpEndDate;
            }
          }
          if (
            (!salary || isNaN(Number(salary)) || Number(salary) <= 0) &&
            detail.ReqPayValue1
          ) {
            const decSalary =
              this.$fun && this.$fun.decryptSalary
                ? this.$fun.decryptSalary(detail.ReqPayValue1)
                : detail.ReqPayValue1;
            if (
              decSalary &&
              !isNaN(Number(decSalary)) &&
              Number(decSalary) > 0
            ) {
              this.form.AgtSalary = String(decSalary);
              salary = decSalary;
            }
          }
        }
      } catch (err) {
        console.warn("Error obteniendo detalle de requerimiento:", err);
      }

      const isProf = Number(item.TypeRequirement) === 1;

      if (salary && !isNaN(Number(salary)) && Number(salary) > 0) {
        this.$fun.alert(
          `Requerimiento #${item.ReqID} (${
            isProf ? "Profesional" : "Masivo"
          }) seleccionado. Sueldo precargado: S/ ${salary}`,
          "info",
        );
      } else {
        if (isProf) {
          this.$fun.alert(
            `Requerimiento #${item.ReqID} (Profesional) seleccionado. Por favor verifique e ingrese el sueldo correspondiente en el formulario.`,
            "warning",
          );
        } else {
          this.$fun.alert(
            `Requerimiento #${item.ReqID} seleccionado. Se han precargado los datos laborales.`,
            "info",
          );
        }
      }
    },

    onCampaignDatesLoaded(dates) {
      if (dates) {
        const beginDate = dates.CmpBeginDate
          ? dates.CmpBeginDate.substring(0, 10)
          : dates.startDate
          ? dates.startDate.substring(0, 10)
          : null;
        const endDate = dates.CmpEndDate
          ? dates.CmpEndDate.substring(0, 10)
          : dates.endDate
          ? dates.endDate.substring(0, 10)
          : null;

        if (beginDate) {
          if (!this.form.AgtAdmissionDate) {
            this.form.AgtAdmissionDate = beginDate;
          }
          if (!this.form.AgtBeginDate) {
            this.form.AgtBeginDate = beginDate;
          }
        }
        this.form.CmpEndDate = endDate;

        if (this.form._isIndeterminate) {
          this.form.AgtEndDate = null;
        } else if (
          this.form._isSeason ||
          this.form.TypeContract === 11 ||
          this.form.TypeContract === "11" ||
          this.form._endDateLocked ||
          !this.form.AgtEndDate
        ) {
          if (endDate) {
            this.form.AgtEndDate = endDate;
          }
        }
      }
    },

    onTypeContractItemsLoaded() {
      if (this.form.TypeContract) {
        this.onTypeContractChanged(this.form.TypeContract);
      }
    },

    onTypeContractChanged(val, item) {
      const rawVal =
        typeof val === "object" && val
          ? val.DedValue || val.GenID || val.value
          : val || this.form.TypeContract;

      let selectedItem = item;
      if (!selectedItem || typeof selectedItem !== "object") {
        const ref = this.$refs.typeContractRef;
        const items =
          ref?.items && ref.items.length > 0
            ? ref.items
            : ref?.$refs?.sgeneric?.items || [];
        selectedItem = items.find(
          (x) =>
            x.DedValue == rawVal ||
            x.GenID == rawVal ||
            x.value == rawVal ||
            x.id == rawVal ||
            x.TypeContract == rawVal,
        );
      }

      let helper2 =
        selectedItem && selectedItem.DedHelper2 !== undefined
          ? selectedItem.DedHelper2
          : selectedItem && selectedItem.dedHelper2 !== undefined
          ? selectedItem.dedHelper2
          : null;

      const desc = (
        selectedItem?.DedDescription ||
        selectedItem?.text ||
        ""
      ).toUpperCase();

      if (
        helper2 === "0" ||
        helper2 === 0 ||
        rawVal === 11 ||
        rawVal === "11" ||
        desc.includes("TEMPORADA")
      ) {
        // Temporada: Bloqueado a fin de campaña y costo variable
        this.form._isSeason = true;
        this.form._endDateLocked = true;
        this.form._isIndeterminate = false;
        this.form.TypeSituation = 2; // Costo Variable
        if (this.form.CmpEndDate) {
          this.form.AgtEndDate = this.form.CmpEndDate;
        }
      } else if (
        helper2 === "2" ||
        helper2 === 2 ||
        desc.includes("INDETERMINADO") ||
        desc.includes("INDEFINIDO")
      ) {
        // Indeterminado: Sin fecha fin y costo fijo
        this.form._isSeason = false;
        this.form._endDateLocked = true;
        this.form._isIndeterminate = true;
        this.form.TypeSituation = 1; // Costo Fijo
        this.form.AgtEndDate = null;
      } else {
        // Plazo Fijo / Otros: Fecha término editable y situación editable
        this.form._isSeason = false;
        this.form._endDateLocked = false;
        this.form._isIndeterminate = false;
        this.form.TypeSituation = 2; // Costo Variable por defecto
      }
    },

    async onPositionChanged(pstID) {
      if (!pstID) return;
      try {
        const res = await _sPositionService.searchByPstID(
          pstID,
          this.$fun.getUserID(),
        );
        if (res && res.data) {
          const position = res.data;
          if (position.TypeLaborRegime)
            this.form.TypeLaborRegime = position.TypeLaborRegime;
          if (position.TypePosition)
            this.form.TypeWorker = position.TypePosition;
          if (position.PstOccupationalCategory)
            this.form.TypeOccupationalCategory =
              position.PstOccupationalCategory;
        }
      } catch (e) {
        console.warn("Error cargando detalles del cargo:", e);
      }
    },

    onWorkersSelectedFromGrid(items) {
      this.gridSelectedWorkers = items || [];
    },

    assignWorkerFromGrid(row) {
      if (!row) return;
      this.addWorkerObjectToBatch(row);
    },

    assignSelectedWorkersFromGrid() {
      if (!this.gridSelectedWorkers || !this.gridSelectedWorkers.length) {
        this.$fun.alert(
          "Seleccione al menos un trabajador en la tabla.",
          "warning",
        );
        return;
      }
      this.gridSelectedWorkers.forEach((w) => {
        this.addWorkerObjectToBatch(w);
      });
    },

    moveGridSelectedToBatch() {
      this.assignSelectedWorkersFromGrid();
    },

    addWorkerObjectToBatch(w) {
      const doc =
        w.PrsDocumentNumber ||
        w.AdmDocumentNumber ||
        w.CrdDocumentNumber ||
        w.PrsDocNumber ||
        w.DocNumber ||
        "";
      const wkrId = w.WkrID || w.NtpID || w.ID;
      const name =
        w.WkrName ||
        w.WorkerFullName ||
        w.WorkerName ||
        w.PrsFullName ||
        w.NtpFullName ||
        (doc ? `Personal DNI: ${doc}` : `Trabajador #${wkrId}`);

      const exists = this.selectedWorkers.some(
        (sw) =>
          String(sw.WkrID) === String(wkrId) ||
          (doc && String(sw.PrsDocumentNumber) === String(doc)),
      );

      if (!exists) {
        this.selectedWorkers.push({
          WkrID: wkrId,
          NtpID: w.NtpID,
          PrsDocumentNumber: doc,
          WorkerFullName: name,
          WorkerName: name,
          HasBank: true,
          HasPension: true,
          HasHealth: true,
          HasOverlap: false,
          IsEligible: true,
        });

        // Trigger validación asíncrona de requisitos
        this.$nextTick(() => {
          this.validateBatchWorkers();
        });
      }
    },

    moveCheckedToBatch() {
      if (!this.checkedAvailableIndexes.length) return;
      const selected = this.checkedAvailableIndexes
        .map((idx) => this.filteredAvailableWorkers[idx])
        .filter(Boolean);

      selected.forEach((w) => {
        this.addWorkerObjectToBatch(w);
      });

      this.checkedAvailableIndexes = [];
      this.checkAllAvailable = false;
    },

    // Traspaso de trabajadores (lote -> disponibles)
    moveCheckedToAvailable() {
      if (!this.checkedBatchIndexes.length) return;
      const selected = this.checkedBatchIndexes
        .map((idx) => this.filteredBatchWorkers[idx])
        .filter(Boolean);

      selected.forEach((worker) => {
        const exists = this.availableWorkers.some(
          (w) =>
            String(w.WkrID) === String(worker.WkrID) ||
            String(w.PrsDocumentNumber) === String(worker.PrsDocumentNumber),
        );
        if (!exists) {
          this.availableWorkers.push(worker);
        }
      });

      // Remover del lote
      const selectedIds = new Set(
        selected.map((s) => String(s.WkrID || s.PrsDocumentNumber)),
      );
      this.selectedWorkers = this.selectedWorkers.filter(
        (w) =>
          !selectedIds.has(String(w.WkrID)) &&
          !selectedIds.has(String(w.PrsDocumentNumber)),
      );
      this.checkedBatchIndexes = [];
      this.checkAllBatch = false;
    },

    toggleSelectAllAvailable(val) {
      if (val) {
        this.checkedAvailableIndexes = this.filteredAvailableWorkers.map(
          (_, i) => i,
        );
      } else {
        this.checkedAvailableIndexes = [];
      }
    },

    toggleSelectAllBatch(val) {
      if (val) {
        this.checkedBatchIndexes = this.filteredBatchWorkers.map((_, i) => i);
      } else {
        this.checkedBatchIndexes = [];
      }
    },

    async validateBatchWorkers() {
      if (!this.selectedWorkers.length) return;

      this.isValidating = true;
      try {
        const wkrIds = this.selectedWorkers
          .map((w) => w.WkrID)
          .filter((id) => !!id && id > 0);
        if (!wkrIds.length) return;

        const req = {
          WkrIDs: wkrIds,
          AgtBeginDate: this.form.AgtBeginDate,
          AgtEndDate: this.form.AgtEndDate,
        };

        const res = await _sCttoService.validateMassiveWorkers(
          req,
          this.$fun.getUserID(),
        );
        if (res && res.data && Array.isArray(res.data)) {
          const validatedMap = new Map();
          res.data.forEach((v) => validatedMap.set(v.WkrID, v));

          this.selectedWorkers = this.selectedWorkers.map((worker) => {
            const val = validatedMap.get(worker.WkrID);
            if (val) {
              return {
                ...worker,
                ...val,
                WorkerFullName:
                  val.WorkerFullName ||
                  worker.WorkerFullName ||
                  worker.WorkerName,
                WorkerName:
                  val.WorkerFullName ||
                  worker.WorkerFullName ||
                  worker.WorkerName,
              };
            }
            return worker;
          });
        }
      } catch (e) {
        console.error("Error al validar trabajadores del lote:", e);
      } finally {
        this.isValidating = false;
      }
    },

    validateForm() {
      if (!this.selectedRequirement || !this.selectedRequirement.ReqID) {
        this.$fun.alert(
          "Debe seleccionar obligatoriamente un Requerimiento en la tabla superior.",
          "warning",
        );
        return false;
      }

      const errors = [];
      if (!this.form.TypePayroll) errors.push("Planilla");
      if (!this.form.AreID) errors.push("Área");
      if (!this.form.CceID) errors.push("Centro de Costo");
      if (!this.form.PstID) errors.push("Cargo");
      if (!this.form.TypeContract) errors.push("Tipo de Contrato");
      if (!this.form.TypeCategory) errors.push("Categoría");
      if (!this.form.TypeWorker) errors.push("Tipo de Trabajador");
      if (!this.form.TypeLaborRegime) errors.push("Régimen Laboral");
      if (!this.form.TypeOccupationalCategory)
        errors.push("Categoría Ocupacional");
      if (!this.form.TypeSituation) errors.push("Situación (Costo)");
      if (!this.form.CmpID) errors.push("Campaña");
      if (!this.form.TypeTurno) errors.push("Turno");

      // Validar Sueldo
      const rawSalary = this.form.AgtSalary;
      let isValidSalary = false;
      if (rawSalary !== null && rawSalary !== undefined && rawSalary !== "") {
        const num = parseFloat(String(rawSalary).trim());
        if (!isNaN(num) && num > 0) {
          isValidSalary = true;
        } else {
          try {
            const decrypted = this.$fun?.decryptSalary
              ? this.$fun.decryptSalary(String(rawSalary).trim())
              : "";
            const decNum = parseFloat(decrypted);
            if (!isNaN(decNum) && decNum > 0) isValidSalary = true;
          } catch (e) {
            isValidSalary = false;
          }
        }
      }
      if (!isValidSalary) errors.push("Sueldo válido (> 0)");

      if (!this.form.AgtAdmissionDate) errors.push("Fecha de Ingreso");
      if (!this.form.AgtBeginDate) errors.push("Inicio del Contrato");

      if (errors.length > 0) {
        this.$fun.alert(
          `Complete los campos obligatorios: ${errors.join(", ")}`,
          "warning",
        );
        return false;
      }

      // Validar que la fecha de inicio no sea menor a la de ingreso (regla de ContractLaborForm)
      const admissionDateStr = String(this.form.AgtAdmissionDate).substring(
        0,
        10,
      );
      const beginDateStr = String(this.form.AgtBeginDate).substring(0, 10);
      if (beginDateStr < admissionDateStr) {
        this.$fun.alert(
          "La Fecha de Inicio del Contrato no puede ser menor a la Fecha de Ingreso.",
          "error",
        );
        return false;
      }

      // Validar fecha fin de contrato
      if (this.form.AgtEndDate) {
        const endDateStr = String(this.form.AgtEndDate).substring(0, 10);
        if (endDateStr < beginDateStr) {
          this.$fun.alert(
            "La Fecha Fin del Contrato no puede ser menor a la Fecha de Inicio.",
            "error",
          );
          return false;
        }
      }

      return true;
    },

    async confirmMassiveSave() {
      if (!this.selectedWorkers.length) {
        this.$fun.alert(
          "Debe agregar al menos un trabajador al lote.",
          "warning",
        );
        return;
      }

      if (!this.validateForm()) return;

      // Revalidar trabajadores antes de confirmar
      await this.validateBatchWorkers();

      const eligibleWorkers = this.selectedWorkers.filter((w) => w.IsEligible);
      const ineligibleCount =
        this.selectedWorkers.length - eligibleWorkers.length;

      if (eligibleWorkers.length === 0) {
        this.$fun.alert(
          "Ninguno de los trabajadores seleccionados está apto para contratación (falta cuenta bancaria, pensión, salud o presentan solapamientos con contratos activos).",
          "error",
        );
        return;
      }

      let confirmMsg = `¿Confirmar la creación de ${eligibleWorkers.length} contrato(s) masivo(s) vinculados al Requerimiento #${this.selectedRequirement.ReqID}?`;
      if (ineligibleCount > 0) {
        confirmMsg += `\n\n(Se omitirán ${ineligibleCount} trabajador(es) con observaciones).`;
      }

      const confirm = await this.$fun.alert(confirmMsg, "question");
      if (!confirm || !confirm.value) return;

      await this.executeMassiveSave(eligibleWorkers);
    },

    async executeMassiveSave(workersToSave) {
      this.isSaving = true;
      try {
        const payload = {
          ContractTemplate: {
            EstID: this.form.EstID ? Number(this.form.EstID) : 1,
            TypePayroll: this.form.TypePayroll
              ? Number(this.form.TypePayroll)
              : null,
            CceID: this.form.CceID ? Number(this.form.CceID) : null,
            AreID: this.form.AreID ? Number(this.form.AreID) : null,
            PstID: this.form.PstID ? Number(this.form.PstID) : null,
            CmpID: this.form.CmpID ? Number(this.form.CmpID) : null,
            TypeContract: this.form.TypeContract
              ? Number(this.form.TypeContract)
              : 11,
            TypeCategory: this.form.TypeCategory
              ? Number(this.form.TypeCategory)
              : 1,
            TypeWorker: this.form.TypeWorker ? Number(this.form.TypeWorker) : 1,
            TypeLaborRegime: this.form.TypeLaborRegime
              ? Number(this.form.TypeLaborRegime)
              : 3,
            TypeOccupationalCategory: this.form.TypeOccupationalCategory
              ? Number(this.form.TypeOccupationalCategory)
              : 4,
            TypeSituation: this.form.TypeSituation
              ? Number(this.form.TypeSituation)
              : 2,
            TypeSpecialSituation: this.form.TypeSpecialSituation
              ? Number(this.form.TypeSpecialSituation)
              : 1,
            TypeTurno: this.form.TypeTurno ? Number(this.form.TypeTurno) : 1,
            GngID: this.form.GngID ? Number(this.form.GngID) : null,
            BslID: this.form.BslID ? Number(this.form.BslID) : null,
            AgtMaximunDay: this.form.AgtMaximunDay ? 1 : 0,
            AgtSalary: String(this.form.AgtSalary || "").trim(),
            AgtAdmissionDate:
              this.form.AgtAdmissionDate ||
              new Date().toISOString().substring(0, 10),
            AgtBeginDate:
              this.form.AgtBeginDate ||
              new Date().toISOString().substring(0, 10),
            AgtEndDate: this.form._isIndeterminate
              ? null
              : this.form.AgtEndDate || null,
            ReqID: this.selectedRequirement
              ? Number(this.selectedRequirement.ReqID)
              : null,
          },
          Workers: workersToSave.map((w) => ({
            WkrID: Number(w.WkrID || 0),
            NtpID: Number(w.NtpID || 0),
            PrsID: Number(w.PrsID || 0),
            PrsDocumentNumber: String(w.PrsDocumentNumber || ""),
            WorkerFullName: String(w.WorkerFullName || w.WorkerName || ""),
            HasBank: !!w.HasBank,
            HasPension: !!w.HasPension,
            HasHealth: !!w.HasHealth,
            HasOverlap: !!w.HasOverlap,
            IsEligible: !w.HasOverlap,
            BatID: w.BatID ? Number(w.BatID) : null,
            TypeEntityBank: w.TypeEntityBank ? Number(w.TypeEntityBank) : null,
            BatAccountNumber: w.BatAccountNumber || null,
            BatCCI: w.BatCCI || null,
            TypePay: w.TypePay ? Number(w.TypePay) : 1,
            TypeMoney: w.TypeMoney ? Number(w.TypeMoney) : 1,
            PsnID: w.PsnID ? Number(w.PsnID) : null,
            TypePensionRegimen: w.TypePensionRegimen
              ? Number(w.TypePensionRegimen)
              : null,
            CUSPP: w.CUSPP || null,
            HthID: w.HthID ? Number(w.HthID) : null,
            TypeHealthRegimen: w.TypeHealthRegimen
              ? Number(w.TypeHealthRegimen)
              : null,
            TypeCompany: w.TypeCompany ? Number(w.TypeCompany) : null,
          })),
        };

        const res = await _sCttoService.saveMassiveContracts(
          payload,
          this.$fun.getUserID(),
        );
        if (res && res.data) {
          this.lastResult = res.data;
          this.showResultDialog = true;
        }
      } catch (e) {
        console.error("Error al ejecutar guardado masivo:", e);
        this.$fun.alert(
          "Ocurrió un error al procesar la contratación masiva.",
          "error",
        );
      } finally {
        this.isSaving = false;
      }
    },

    onFinishProcess() {
      this.showResultDialog = false;
      const successfulDocs = (this.lastResult.Details || [])
        .filter((d) => d.Success)
        .map((d) => d.PrsDocumentNumber);

      this.selectedWorkers = this.selectedWorkers.filter(
        (w) => !successfulDocs.includes(w.PrsDocumentNumber),
      );

      if (this.selectedWorkers.length === 0) {
        this.$fun.alert(
          "Contratación masiva completada exitosamente.",
          "success",
        );
        this.goBack();
      }
    },

    // ==========================================
    // MÉTODOS PARA TAB 1: RENOVACIONES Y AMPLIACIONES (CON S-CRUD)
    // ==========================================
    onActiveWorkersSelectedFromGrid(workers) {
      this.selectedActiveWorkers = (workers || []).map((w) => ({
        WkrID: w.WkrID,
        AgtID: w.CtrID || w.AgtID || 0,
        CtrID: w.CtrID || w.AgtID || 0,
        PrsID: w.PrsID,
        NtpID: w.NtpID,
        PrsDocumentNumber: w.PrsDocumentNumber || "",
        WorkerFullName: w.WkrName || w.WorkerFullName || "",
        WkrName: w.WkrName || w.WorkerFullName || "",
        NtpFullName: w.WkrName || w.WorkerFullName || "",
        AreName: w.AreName || "",
        CtrBeginDate: w.CtrBeginDate || w.AgtBeginDate,
        CtrEndDate: w.CtrEndDate || w.AgtEndDate,
        AgtBeginDate: w.CtrBeginDate || w.AgtBeginDate,
        AgtEndDate: w.CtrEndDate || w.AgtEndDate,
      }));
    },

    removeSelectedActiveWorker(index) {
      this.selectedActiveWorkers.splice(index, 1);
    },

    clearActiveWorkersSelection() {
      this.selectedActiveWorkers = [];
      if (
        this.$refs.gridActiveWorker &&
        this.$refs.gridActiveWorker.clearSelection
      ) {
        this.$refs.gridActiveWorker.clearSelection();
      }
    },

    isWorkerExtensionValid(worker) {
      if (!this.renewalForm.newEndDate) return null;
      const currentEnd =
        worker.CtrEndDate ||
        worker.AgtEndDate ||
        worker.CtrBeginDate ||
        worker.AgtBeginDate;
      if (!currentEnd) return true;
      const currEndStr = String(currentEnd).substring(0, 10);
      return this.renewalForm.newEndDate > currEndStr;
    },

    isWorkerCessationValid(worker) {
      if (!this.renewalForm.terminationDate) return null;
      const begin = worker.CtrBeginDate || worker.AgtBeginDate;
      if (!begin) return true;
      const beginStr = String(begin).substring(0, 10);
      return this.renewalForm.terminationDate >= beginStr;
    },

    async confirmMassiveRenewalExtension() {
      if (
        !this.selectedActiveWorkers ||
        this.selectedActiveWorkers.length === 0
      ) {
        this.$fun.alert("Debe seleccionar al menos un trabajador.", "warning");
        return;
      }

      const isExtension = this.renewalForm.operationType === "EXTENSION";
      const isRenovation = this.renewalForm.operationType === "RENOVATION";
      const isCessation = this.renewalForm.operationType === "CESSATION";

      if (isExtension) {
        if (!this.renewalForm.newEndDate) {
          this.$fun.alert("Debe ingresar la nueva fecha de fin.", "warning");
          return;
        }

        const invalidWorkers = this.selectedActiveWorkers.filter(
          (w) => this.isWorkerExtensionValid(w) === false,
        );

        if (invalidWorkers.length === this.selectedActiveWorkers.length) {
          this.$fun.alert(
            "Ninguno de los trabajadores seleccionados tiene una fecha fin menor a la nueva fecha de ampliación.",
            "error",
          );
          return;
        }

        let msg = `¿Confirmar la ampliación de contrato para ${
          this.selectedActiveWorkers.length
        } trabajador(es) hasta el ${this.$fun.formatDateView(
          this.renewalForm.newEndDate,
        )}?`;
        if (invalidWorkers.length > 0) {
          msg += `\n\n(Aviso: ${invalidWorkers.length} trabajador(es) tienen fecha fin mayor o igual a la nueva fecha y serán reportados como observación por el sistema).`;
        }

        const confirm = await this.$fun.alert(msg, "question");
        if (!confirm || !confirm.value) return;

        await this.executeMassiveRenewalExtension();
      } else if (isRenovation) {
        // RENOVACIÓN
        if (!this.renewalForm.newBeginDate || !this.renewalForm.newEndDate) {
          this.$fun.alert(
            "Debe ingresar la fecha de inicio y fin para la renovación.",
            "warning",
          );
          return;
        }

        if (this.renewalForm.newEndDate < this.renewalForm.newBeginDate) {
          this.$fun.alert(
            "La fecha fin no puede ser menor a la fecha de inicio.",
            "error",
          );
          return;
        }

        const d = new Date(this.renewalForm.newBeginDate + "T00:00:00");
        d.setDate(d.getDate() - 1);
        const prevTerminationStr = this.$fun.formatDateView(
          d.toISOString().substring(0, 10),
        );

        const msg = `¿Confirmar la RENOVACIÓN masiva para ${
          this.selectedActiveWorkers.length
        } trabajador(es)?\n\n• Nuevo Contrato: Del ${this.$fun.formatDateView(
          this.renewalForm.newBeginDate,
        )} al ${this.$fun.formatDateView(
          this.renewalForm.newEndDate,
        )} (Estado: Borrador AgtStatus = 0).\n• Contrato Anterior: Pasará a BAJA con fecha de cese ${prevTerminationStr}.\n• Se heredarán todas las condiciones laborales activas (Sueldo, Planilla, Banco, AFP, Salud).`;

        const confirm = await this.$fun.alert(msg, "question");
        if (!confirm || !confirm.value) return;

        await this.executeMassiveRenewalExtension();
      } else if (isCessation) {
        // BAJA MASIVA
        if (!this.renewalForm.terminationDate) {
          this.$fun.alert("Debe ingresar la fecha de baja / cese.", "warning");
          return;
        }

        const invalidWorkers = this.selectedActiveWorkers.filter(
          (w) => this.isWorkerCessationValid(w) === false,
        );

        if (invalidWorkers.length === this.selectedActiveWorkers.length) {
          this.$fun.alert(
            "Ninguno de los trabajadores seleccionados tiene una fecha de inicio menor o igual a la fecha de cese.",
            "error",
          );
          return;
        }

        let msg = `¿Confirmar la BAJA MASIVA para ${
          this.selectedActiveWorkers.length
        } trabajador(es) al día ${this.$fun.formatDateView(
          this.renewalForm.terminationDate,
        )}?\n\n• Los contratos activos pasarán a estado BAJA.\n• El periodo laboral se marcará como cerrado/inactivo.\n• Se generará el movimiento de cese correspondiente.`;

        if (invalidWorkers.length > 0) {
          msg += `\n\n(Aviso: ${invalidWorkers.length} trabajador(es) tienen fecha de inicio posterior a la fecha de cese y serán observados).`;
        }

        const confirm = await this.$fun.alert(msg, "question");
        if (!confirm || !confirm.value) return;

        await this.executeMassiveRenewalExtension();
      }
    },

    async executeMassiveRenewalExtension() {
      this.isProcessingRenewalExtension = true;
      try {
        const isCessation = this.renewalForm.operationType === "CESSATION";
        const isRenovation = this.renewalForm.operationType === "RENOVATION";

        const payload = {
          OperationType: this.renewalForm.operationType,
          NewBeginDate: isRenovation ? this.renewalForm.newBeginDate : null,
          NewEndDate: isCessation ? null : this.renewalForm.newEndDate,
          TerminationDate: isCessation
            ? this.renewalForm.terminationDate
            : null,
          TypeTermination: isCessation
            ? this.renewalForm.typeTermination || null
            : null,
          Workers: this.selectedActiveWorkers.map((w) => ({
            WkrID: Number(w.WkrID || 0),
            AgtID: Number(w.AgtID || 0),
            PrsDocumentNumber: String(w.PrsDocumentNumber || ""),
            WorkerFullName: String(w.NtpFullName || w.WorkerFullName || ""),
          })),
        };

        const res = await _sCttoService.saveMassiveRenewalsExtensions(
          payload,
          this.$fun.getUserID(),
        );

        if (res && res.data) {
          this.renewalResult = res.data;
          this.showRenewalResultDialog = true;
        }
      } catch (e) {
        console.error(
          "Error al procesar renovación/ampliación/baja masiva:",
          e,
        );
        this.$fun.alert(
          "Ocurrió un error al procesar la operación masiva.",
          "error",
        );
      } finally {
        this.isProcessingRenewalExtension = false;
      }
    },

    onFinishRenewalProcess() {
      this.showRenewalResultDialog = false;
      this.clearActiveWorkersSelection();

      if (this.$refs.gridActiveWorker && this.$refs.gridActiveWorker.reload) {
        this.$refs.gridActiveWorker.reload();
      }

      this.$fun.alert("Operación completada exitosamente.", "success");
    },
  },
};
</script>

<style scoped>
.contract-massive-screen {
  max-width: 1550px;
  margin: 0 auto;
}
.selected-req-row {
  background-color: #e3f2fd !important;
}
.requirement-table >>> th {
  font-size: 11px !important;
  font-weight: bold !important;
}
.requirement-table >>> td {
  font-size: 12px !important;
}
.worker-list-scroll {
  max-height: 250px;
  overflow-y: auto;
}
.border-top {
  border-top: 1px solid #e0e0e0;
}
.border-bottom {
  border-bottom: 1px solid #e0e0e0;
}
.min-height-32 {
  min-height: 32px;
}
.caption-input >>> input {
  font-size: 12px !important;
}
.x-small {
  font-size: 10px !important;
}
.gap-1 {
  gap: 4px;
}
.gap-2 {
  gap: 8px;
}
.h-100 {
  height: 100%;
}
</style>
